/**
 * pageNames.ts
 *
 * Resolves a human-readable page name for any route.
 *
 * Pages are supposed to register themselves with the assistant via
 * usePageContext(), but roughly 137 of ~190 routed pages never did — mostly
 * the /admin/* tree and the marketing/report detail layers, plus anything
 * added recently. On those pages the sidebar used to describe whatever page
 * the user had visited *before*.
 *
 * Rather than sprinkle a hook call across every page (and rely on remembering
 * it for every future page), the sidebar resolves the name from the route.
 * Nav labels are reused from navigation.config so there's one source of truth,
 * PATTERN_LABELS covers routes that aren't nav destinations, and a humanizer
 * catches everything else.
 *
 * A page that DOES call usePageContext still wins — see useResolvedPageContext.
 */
import { NAV_ITEMS, type NavItemDef } from '@/components/layout/navigation.config';

/** href → label, flattened from the nav tree (includes nested children). */
const NAV_LABELS: Record<string, string> = (() => {
  const out: Record<string, string> = {};
  const walk = (items: NavItemDef[]) => {
    for (const item of items) {
      if (item.href && !item.external && !out[item.href]) {
        out[item.href] = item.label;
      }
      if (item.children) walk(item.children);
    }
  };
  walk(NAV_ITEMS);
  return out;
})();

/**
 * Labels for routes that aren't nav destinations — detail views, editors and
 * wizards. `:param` matches any single segment.
 */
const PATTERN_LABELS: Array<[string, string]> = [
  // Reports
  ['/reports/builder/new', 'Report Builder'],
  ['/reports/builder/:id', 'Report Builder'],
  ['/reports/orders/:id/edit', 'Report Builder'],
  ['/reports/orders', 'Scheduled Report Orders'],
  ['/reports/my-reports', 'My Reports'],
  ['/reports/history', 'On-Demand Report History'],
  ['/reports/results/:id', 'Report Result'],
  ['/reports/presets', 'Report Presets'],

  // Marketing — email
  ['/marketing/email-campaigns', 'Email Campaigns'],
  ['/marketing/campaigns/:id', 'Email Campaign Editor'],
  ['/marketing/campaigns/:id/scheduled', 'Email Campaign Scheduled'],
  ['/marketing/campaigns/:id/stats', 'Email Campaign Stats'],
  ['/marketing/campaigns/:id/contacts', 'Email Campaign Contacts'],
  ['/marketing/campaigns/:id/excludes', 'Email Campaign Excludes'],
  ['/marketing/templates/email/:id', 'Email Template Builder'],
  ['/marketing/automations/email/:id', 'Email Automation Editor'],
  ['/marketing/automations/email/:id/stats', 'Email Automation Stats'],
  ['/marketing/automations/email/:id/contacts', 'Email Automation Contacts'],

  // Marketing — SMS
  ['/marketing/sms-campaigns/:id', 'SMS Campaign Editor'],
  ['/marketing/sms-campaigns/:id/scheduled', 'SMS Campaign Scheduled'],
  ['/marketing/sms-campaigns/:id/stats', 'SMS Campaign Stats'],
  ['/marketing/sms-campaigns/:id/contacts', 'SMS Campaign Contacts'],
  ['/marketing/sms-campaigns/:id/sent-contacts', 'SMS Campaign Sent Contacts'],
  ['/marketing/sms-campaigns/:id/excludes', 'SMS Campaign Excludes'],
  ['/marketing/templates/sms/:id', 'SMS Template Builder'],
  ['/marketing/automations/sms/:id', 'SMS Automation Editor'],
  ['/marketing/automations/sms/:id/stats', 'SMS Automation Stats'],
  ['/marketing/automations/sms/:id/contacts', 'SMS Automation Contacts'],

  // Marketing — other
  ['/marketing/push-campaigns/:id', 'Push Campaign Editor'],
  ['/marketing/ai-explorer', 'Marketing Explorer'],
  ['/marketing/settings', 'Marketing Settings'],
  ['/marketing/contact-lists/:id', 'Contact List Contacts'],
  ['/marketing/contact-lists/:id/details', 'Contact List Detail'],

  // CRM / leads / quotes / plans
  ['/leads/new', 'New Lead'],
  ['/leads/:id', 'Lead Detail'],
  ['/crm/pipeline', 'CRM Pipeline'],
  ['/plans/:id', 'Treatment Plan'],
  ['/patients/:id', 'Patient Detail'],
  ['/patients/:id/plans/:planId', 'Patient Treatment Plan'],

  // Forms / surveys
  ['/surveys/all', 'Forms Inbox'],
  ['/surveys/:id/build', 'Form Builder'],
  ['/surveys/:id/responses', 'Form Responses'],

  // Help / knowledge
  ['/help/tickets/:id', 'Support Ticket'],
  ['/knowledge/article/:id', 'Knowledge Article'],
  ['/knowledge/library', 'Knowledge Library'],
  ['/knowledge/announcements', 'My Announcements'],
  ['/announcements', 'Announcements Archive'],

  // Admin — knowledge base sub-router
  ['/admin/knowledge-base/library', 'Knowledge Base Library'],
  ['/admin/knowledge-base/library/new', 'New Knowledge Base Document'],
  ['/admin/knowledge-base/library/:id', 'Knowledge Base Document'],
  ['/admin/knowledge-base/library/:id/edit', 'Edit Knowledge Base Document'],
  ['/admin/knowledge-base/chapters', 'Knowledge Base Chapters'],
  ['/admin/knowledge-base/chapters/:id', 'Knowledge Base Chapter'],
  ['/admin/knowledge-base/assignments', 'Knowledge Base Assignments'],
  ['/admin/knowledge-base/quizzes', 'Knowledge Base Quizzes'],
  ['/admin/knowledge-base/quizzes/:id', 'Quiz Builder'],
  ['/admin/knowledge-base/announcements', 'Knowledge Base Announcements'],
  ['/admin/knowledge-base/audit', 'Knowledge Base Audit'],
  ['/admin/knowledge-base/sources', 'Knowledge Base Sources'],

  // Admin — other non-nav routes
  ['/admin/scorecards/:id', 'Scorecard Editor'],
  ['/admin/account-summary', 'Account Details'],

  // Misc
  ['/debug/patient-product-types', 'Patient Product Types (Debug)'],
];

/** Segment looks like a record id rather than a meaningful path part. */
function isIdSegment(segment: string): boolean {
  return /^\d+$/.test(segment) || /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(segment);
}

function matchesPattern(pattern: string, segments: string[]): boolean {
  const patternSegments = pattern.split('/').filter(Boolean);
  if (patternSegments.length !== segments.length) return false;
  return patternSegments.every(
    (p, i) => p.startsWith(':') || p.toLowerCase() === segments[i].toLowerCase(),
  );
}

function humanize(segments: string[]): string {
  const meaningful = segments.filter((s) => !isIdSegment(s));
  if (meaningful.length === 0) return 'This page';
  return meaningful
    .map((s) =>
      s
        .replace(/[-_]+/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
        .trim(),
    )
    .join(' ');
}

/**
 * Best available human-readable name for a route. Never throws and always
 * returns something usable.
 */
export function resolvePageName(pathname: string): string {
  const path = pathname.replace(/\/+$/, '') || '/';

  if (NAV_LABELS[path]) return NAV_LABELS[path];

  const segments = path.split('/').filter(Boolean);

  // Most specific pattern wins, so /marketing/campaigns/:id/stats beats
  // /marketing/campaigns/:id.
  let best: string | null = null;
  let bestSpecificity = -1;
  for (const [pattern, label] of PATTERN_LABELS) {
    if (!matchesPattern(pattern, segments)) continue;
    const specificity = pattern.split('/').filter((p) => p && !p.startsWith(':')).length;
    if (specificity > bestSpecificity) {
      best = label;
      bestSpecificity = specificity;
    }
  }
  if (best) return best;

  // Fall back to the closest nav ancestor, then to the raw path.
  for (let i = segments.length - 1; i > 0; i--) {
    const ancestor = `/${segments.slice(0, i).join('/')}`;
    if (NAV_LABELS[ancestor]) {
      const tail = humanize(segments.slice(i));
      return tail === 'This page' ? NAV_LABELS[ancestor] : `${NAV_LABELS[ancestor]} — ${tail}`;
    }
  }

  return humanize(segments);
}
